import { formatDateMMDDyyyy } from '../../util/DateUtils';
import type { AssistantAudience } from './assistantCapabilities';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';
import {
  searchBookingByNumber,
  type BookingLookupRow,
} from './requests/booking-lookup-requests';

/**
 * Explains why a booking's payment failed, looked up by its order number.
 *
 * ## Why a hand-written skill
 *
 * The backend agent answered this wrongly — "I could not find any booking or
 * invoice records for booking #1021445" — for a booking that plainly exists,
 * because the number a user types is the *order number*, not the internal id the
 * by-id endpoints take. This skill searches by order number, so it finds the
 * booking the user actually means, and reads its real status instead of guessing.
 *
 * ## Why it does not invent a cause
 *
 * A failed payment on SecurSpace means a charge could not be collected — the
 * product's own wording is "insufficient funds or a bank dispute", and the
 * booking cannot be used until the buyer submits payment. That is the honest,
 * account-independent explanation, so the skill states it for a genuinely failed
 * booking and, crucially, does *not* claim a failure when the booking is in some
 * other state (approved, cancelled, declined). Reporting a status the booking
 * does not have would be worse than the agent's miss.
 */

/** The words a user uses to name the number: "booking 1021445", "order #1021445". */
const NUMBER_AFTER_KEYWORD = /(?:booking|order|invoice|confirmation|#)\s*#?\s*(\d{4,})/i;

/** Any standalone run of digits long enough to be an order number. */
const BARE_NUMBER = /\b(\d{4,})\b/;

/**
 * Pulls the booking/order number from the message.
 *
 * Prefers a number tied to a keyword ("booking 1021445") so that, in a sentence
 * with more than one number, it takes the one the user labelled. Falls back to
 * any long-enough digit run, which covers "why did payment fail on 1021445".
 */
export const extractBookingNumber = (message: string): string | null => {
  const keyed = message.match(NUMBER_AFTER_KEYWORD);
  if (keyed) return keyed[1];

  const bare = message.match(BARE_NUMBER);
  if (bare) return bare[1];

  return null;
};

const norm = (value?: string): string => (value ?? '').trim().toLowerCase();

/**
 * True when a booking's status is a failed/declined payment.
 *
 * Covers the raw statuses (`Payment Declined`, `Payment Declined-Adjusted`,
 * `Error`) and the buyer-facing display wording (`Payment Denied`). `Error` is a
 * payment failure on a booking: it is what a recurring charge that could not be
 * collected settles to, which is exactly the case in the product's own banner.
 */
const isPaymentFailure = (status?: string): boolean => {
  const s = norm(status);
  if (!s) return false;
  return (
    s === 'error' ||
    s.includes('payment declined') ||
    s.includes('payment denied') ||
    (s.includes('payment') && s.includes('fail'))
  );
};

/** True for statuses where the booking is over, so no payment is outstanding. */
const isInactiveStatus = (status?: string): boolean => {
  const s = norm(status);
  return (
    s.includes('cancel') ||
    s.includes('expire') ||
    (s.includes('declined') && !s.includes('payment'))
  );
};

const bookingLabel = (row: BookingLookupRow): string => {
  const at = row.locationName ? ` at ${row.locationName}` : '';
  const from = row.supplierCompanyName ? ` (${row.supplierCompanyName})` : '';
  return `#${row.orderNumber ?? ''}${at}${from}`.trim();
};

/** The next step for a failed payment, phrased for who is asking. */
const nextSteps = (audience: AssistantAudience): string => {
  if (audience === 'buyer') {
    return (
      'To fix it, open **Bookings \u2192 Payment Failed**, choose the booking and select ' +
      '**Make Payment** \u2014 update the payment method if the card or bank account has ' +
      'changed, then resubmit. The booking becomes usable again once the payment clears.'
    );
  }
  return (
    'The buyer has to submit payment before the booking can be used again \u2014 they can do ' +
    'this from **Bookings \u2192 Payment Failed \u2192 Make Payment**. You can see it under the ' +
    'Payment Failed bookings, and SecurSpace support can help follow up if it stays stuck.'
  );
};

const failedPaymentAnswer = (row: BookingLookupRow, audience: AssistantAudience): KnowledgeAnswer => {
  const dates =
    row.startDate && row.endDate
      ? ` (${formatDateMMDDyyyy(row.startDate)} \u2013 ${formatDateMMDDyyyy(row.endDate)})`
      : '';
  return {
    text:
      `Booking **${bookingLabel(row)}**${dates} has a **failed payment** \u2014 its status is ` +
      `**${row.status}**. That happens when a scheduled charge cannot be collected, almost ` +
      'always **insufficient funds or a bank dispute** on the payment method. The booking is on ' +
      'hold and cannot be used until the payment is submitted.\n\n' +
      nextSteps(audience),
  };
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { message, audience } = context;
  const number = extractBookingNumber(message);

  if (!number) {
    return {
      text:
        'Which booking do you mean? Tell me its booking number \u2014 for example ' +
        '_"why did payment fail on booking 1021445"_ \u2014 and I will look up why the ' +
        'payment failed.',
    };
  }

  const response = await searchBookingByNumber(audience, number);
  const rows = response?.body?.content ?? [];
  const match = rows.find((row) => norm(row.orderNumber) === norm(number)) ?? rows[0];

  if (!match) {
    return {
      text:
        `I could not find a booking numbered **#${number}** on this account. Double-check the ` +
        'number from your Bookings list \u2014 the booking number is the long digits shown as ' +
        '**BOOKING NUMBER** \u2014 or reach out to SecurSpace support and we can trace it.',
    };
  }

  if (isPaymentFailure(match.status)) {
    return failedPaymentAnswer(match, audience);
  }

  if (isInactiveStatus(match.status)) {
    return {
      text:
        `Booking **${bookingLabel(match)}** is **${match.status}**, so there is no failed ` +
        "payment on it right now. If you expected a charge to have failed, it may be on a " +
        'different booking \u2014 tell me that booking number and I will check.',
    };
  }

  return {
    text:
      `Booking **${bookingLabel(match)}** is **${match.status}** \u2014 its payments are up to ` +
      'date, so nothing has failed on it. If a payment you expected to fail is on another ' +
      'booking, give me that booking number and I will take a look.',
  };
};

/**
 * Available to every audience, with the endpoint chosen from the account type:
 * a buyer's own payment failed, a supplier or admin is looking into a buyer's.
 * The booking search each audience is entitled to is scoped by the backend, so
 * none can read a booking outside their reach.
 */
export const bookingPaymentFailureQuery: KnowledgeQuery = {
  id: 'bookingPaymentFailure',
  audiences: ['buyer', 'supplier', 'admin'],
  synonyms: [
    'why did payment fail on booking',
    'why did the payment fail on booking',
    'why did my payment fail on booking',
    'why did payment fail for booking',
    'why did payment fail',
    'why did my payment fail',
    'why did the payment fail',
    'why did the charge fail',
    'why did my charge fail',
    'why was payment declined',
    'why was my payment declined',
    'why was the payment declined',
    'why did payment not go through',
    'why is my payment failing',
    'payment failed on booking',
    'payment failed for booking',
    'why did this booking fail payment',
    'why is this booking payment failed',
  ],
  requires: [
    ['payment', 'charge', 'pay', 'billing'],
    ['fail', 'failed', 'failing', 'declined', 'denied', 'error', 'not'],
  ],
  example: 'why did payment fail on booking 1021445',
  description:
    'Diagnoses why a specific booking\u2019s payment failed, looked up by its order number ' +
    '(e.g. "why did payment fail on booking 1021445"). Reads the booking\u2019s real status and, ' +
    'when it is a failed/declined payment, explains the cause (insufficient funds or a bank ' +
    'dispute) and how to fix it. A diagnostic for one named booking \u2014 not a count, not a ' +
    'revenue figure, and distinct from "why was my booking declined", which is about a supplier ' +
    'rejecting a booking rather than a charge failing to collect.',
  answer,
};

export const __testing = {
  extractBookingNumber,
  isPaymentFailure,
  isInactiveStatus,
  bookingLabel,
};
